import { css } from "@/utils/css";
import { __ } from "@wordpress/i18n";
import { Table } from "@/components/table";
import { Button } from "@/components/button";
import { useMemo, useState } from "@wordpress/element";
import { toast } from "react-toastify";
import { api } from "@/config/api-fetch";
import { KbType, useKnowledgeBase, useKnowledgeBaseStats } from "@/hooks/use-knowledge-base";
import { useModal } from "@/hooks/use-modal";
import { TextareaInput } from "@/components/textarea-input";
import { useProModal } from "@/components/pro-alert-box";
import { IS_PRO } from "@/utils/constants";

import { useRoute } from "@/hooks/use-route";
import { getPaginatedList } from "@/utils/paginate";
import { OPTIONS } from "@/utils/constants";

const MAX_LENGTH = 5000;

export function CustomText() {
    const { query, updateQuery } = useRoute();
    const page = query.page_no ? parseInt(query.page_no, 10) : 1;
    const limit = query.limit ? parseInt(query.limit, 10) : OPTIONS.PAGINATION_LIMIT;
    const search = query.search || "";
    const orderby = query.orderby || "";
    const order = (query.order as any) || "";

    const { refetch: refetchStats } = useKnowledgeBaseStats();
    const { data, refetch, isLoading } = useKnowledgeBase(KbType.txt);
    const [submitting, setSubmitting] = useState(false);
    const [content, setContent] = useState<string>();
    const { confirm } = useModal();
    const { showProAlert } = useProModal();
    const [deletingIds, setDeletingIds] = useState<string[]>([]);

    const deleteFromKb = async (id: string) => {
        setDeletingIds((prev) => [...prev, id]);

        try {
            await api.delete(`/knowledge-base?knowledge_id=${id}`);
            await refetch();
            await refetchStats().catch(() => {});
            toast.success(__("Content has been deleted from the knowledge base.", "antimanual"));
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setDeletingIds((prev) => prev.filter((prevId) => prevId !== id));
    };

    const handleDelete = async (id: string) => {
        const confirmed = await confirm(__("Do you want the AI to forget the information of this content?", "antimanual"));

        if (confirmed) {
            await deleteFromKb(id);
        }
    };

    const handleBulkDelete = async (ids: string[]) => {
        if (ids.length === 0) {
            toast.error(__("Please select at least one Custom Text.", "antimanual"));
            return;
        }

        const confirmed = await confirm(
            __("All of the selected content will be removed from the knowledge base. Do you still want to do that?", "antimanual")
        );

        if (!confirmed) return;

        for (const id of ids) {
            await deleteFromKb(id);
        }
    };

    const columns = [
        { id: "content", label: __("Content", "antimanual"), sortable: true },
        {
            id: "action",
            label: __("Action", "antimanual"),
        },
    ];

    const rows = useMemo(() => {
        if (!data) return [];
        let items = data;

        // Search
        if (search) {
            items = items.filter((item) => item.title.toLowerCase().includes(search.toLowerCase()));
        }

        // Sort
        if (orderby === 'content' && order) {
             items = [...items].sort((a, b) => {
                 return order === 'ASC' ? a.title.localeCompare(b.title) : b.title.localeCompare(a.title);
             });
        }

        const paginatedItems = getPaginatedList(items, page, limit);

        return paginatedItems.map((item) => ({
            id: item.id,
            content: item.title,
            action: (
                <Button small variant="danger" loading={deletingIds.includes(item.id)} onClick={() => handleDelete(item.id)}>
                    {__("Delete", "antimanual")}
                </Button>
            ),
        }));
    }, [data, deletingIds, search, orderby, order, page, limit]);

    const handleContentChange = (content?: string) => {
        setContent(content);
    };

    const handleSubmit = async (text?: string) => {
        const content = text?.trim();

        if (!content) {
            toast.error(__("Content is empty.", "antimanual"));
            return;
        }

        if (content.length > MAX_LENGTH) {
            toast.error(__("Content exceeds maximum length.", "antimanual"));
            return;
        }

        setSubmitting(true);

        try {
            await api.post("/knowledge-base/txt", { content });
            await refetch();
            await refetchStats().catch(() => {});
            toast.success(__("Custom text has been added to the knowledge base.", "antimanual"));
            handleContentChange("");
        } catch (e: any) {
            toast.error(e?.message || __("Something went wrong", "antimanual"));
        }

        setSubmitting(false);
    };

    return (
        <div className={css("kb-option txt-kb")}>
            <div className={css("max-w-3xl mx-auto flex flex-col gap-4 mb-8")}>
                <TextareaInput
                    value={content}
                    onChange={handleContentChange}
                    label={__("Custom Text", "antimanual")}
                    placeholder={__("Type the custom text here...", "antimanual")}
                    maxLength={MAX_LENGTH}
                    rows={8}
                    help={__(`${MAX_LENGTH - (content?.length || 0)} out of ${MAX_LENGTH} characters remaining.`, "antimanual")}
                />
                <Button
                    className={css("min-w-xs px-8 py-3")}
                    onClick={() => {
                        if (!IS_PRO) {
                            showProAlert();
                            return;
                        }

                        handleSubmit(content);
                    }}
                    loading={submitting}
                    disabled={submitting || !content || (content ? content.length > MAX_LENGTH : false)}
                >
                    {__("Add to knowledge base", "antimanual")}
                </Button>
            </div>
            <Table
                rowsLoading={isLoading}
                columns={columns}
                rows={rows}
                actions={[{ value: "delete", label: __("Delete", "antimanual"), handler: handleBulkDelete }]}
                selectedAction="delete"
                pagination={{
                    total: (() => {
                         if (!data) return 0;
                         if (search) return data.filter((item) => item.title.toLowerCase().includes(search.toLowerCase())).length;
                         return data.length;
                    })(),
                    currentPage: page,
                    limit: limit,
                    onPageChange: (page) => updateQuery({ page_no: `${page}` }),
                    onLimitChange: (limit) => updateQuery({ limit: `${limit}`, page_no: "1" }),
                }}
                search={search}
                onSearch={(search) => updateQuery({ search, page_no: "1" })}
                sorting={{ orderby, order }}
                onSort={(orderby, order) => updateQuery({ orderby, order, page_no: "1" })}
            />
        </div>
    );
}
